import { selectFileProcessingStatusesWithKeys } from '@/store/selectors/file-upload-tasks-reducers.selector'
import { FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { RootState } from '@/store/store'
import { createSelector } from '@reduxjs/toolkit'
import { selectLoadedUserFilesById } from '@/user_files/files/store/selectors'
import { getUserFileAttachmentStatus, type UserFileAttachmentStatus } from '@/chat-common/selected_files/utils/userFileUploadStatus'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import type { PendingUpload } from './slice'
import {
  getFederalCourtIdsByState,
  getStandardFederalLRRSources,
  inferSelectedStateFromCourtIds,
  getStandardFederalCourtIds,
  getStateLrrSources,
} from '@/jurisdiction-mappings/jurisdiction-maps'
import dayjs from 'dayjs'

/**
 * Select Agent Conversation Form User File IDs
 * Get the list of selected user file ids for a conversation.
 */
export const selectAgentConversationFormUserFileIds = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.user_files_rag?.user_file_ids ?? []
)

/**
 * Select Agent Conversation Form Writing Sample File Ids
 * Get the user_file ids selected as writing samples (new files UX) for a conversation.
 *
 * @returns The list of writing-sample user_file ids
 */
export const selectAgentConversationFormWritingSampleFileIds = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.writing_sample_files?.user_file_ids ?? []
)

/**
 * Select Agent Conversation Form User Files RAG Request
 * Get the active user-files request object, preserving null vs empty selection.
 */
export const selectAgentConversationFormUserFilesRag = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.user_files_rag ?? null
)

export const selectAgentConversationFormPendingUploads = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.pendingUploadsByConversationId[props.conversationId],

  (pendingUploads): PendingUpload[] => pendingUploads ?? []
)

// Whether to show the files-reset notice for this conversation.
export const selectAgentConversationFormShowFilesResetNotice = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.showFilesResetNoticeByConversationId[props.conversationId],
  (showNotice) => showNotice ?? false
)

export const selectAgentConversationFormAttachedPendingUploads = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.attachedPendingUploadsByConversationId[props.conversationId],

  (pendingUploads): PendingUpload[] => pendingUploads ?? []
)

// A pending upload joined to its live upload state (target rides along so the chip can render in the right slot).
export type ResolvedPendingUpload = PendingUpload & { uploadState?: FileUploadState }

export const selectAgentConversationFormPendingUserFileUploads = createSelector(
  [selectAgentConversationFormPendingUploads, selectAllFiles],
  (pendingUploads, uploadFilesByClientTmpId): ResolvedPendingUpload[] =>
    pendingUploads.map((upload) => ({
      ...upload,
      uploadState: uploadFilesByClientTmpId[upload.clientTmpId],
    }))
)

const selectAgentConversationFormAttachedPendingUserFileUploads = createSelector(
  [selectAgentConversationFormAttachedPendingUploads, selectAllFiles],
  (pendingUploads, uploadFilesByClientTmpId): ResolvedPendingUpload[] =>
    pendingUploads.map((upload) => ({
      ...upload,
      uploadState: uploadFilesByClientTmpId[upload.clientTmpId],
    }))
)

const selectAgentConversationFormUserFileAttachments = createSelector(
  [selectAgentConversationFormUserFileIds, selectAgentConversationFormWritingSampleFileIds, selectUploadFilesByServerFileId, selectLoadedUserFilesById],
  // Writing samples are user_files too, so include them in readiness/processing/error checks —
  // an unready writing sample should block submit and show the processing/error state, just like
  // a RAG user-file selection.
  (userFileIds, writingSampleFileIds, uploadFilesByServerFileId, loadedUserFilesById) =>
    Array.from(new Set([...userFileIds, ...writingSampleFileIds])).map((fileId) => {
      const uploadState = uploadFilesByServerFileId[fileId]
      return {
        fileId,
        uploadState,
        file: uploadState?.backend?.record ?? loadedUserFilesById[fileId],
      }
    })
)

const selectAgentConversationFormAttachedUserFileAttachmentStatuses = createSelector(
  [selectAgentConversationFormUserFileAttachments, selectAgentConversationFormAttachedPendingUserFileUploads],
  (attachments, pendingUploads): UserFileAttachmentStatus[] => [
    ...attachments.map(({ file, uploadState }) => getUserFileAttachmentStatus({ file, uploadState }).status),
    ...pendingUploads.map(({ uploadState }) => getUserFileAttachmentStatus({ uploadState }).status),
  ]
)

const selectAgentConversationFormVisibleUserFileAttachmentStatuses = createSelector(
  [selectAgentConversationFormUserFileAttachments, selectAgentConversationFormPendingUserFileUploads],
  (attachments, pendingUploads): UserFileAttachmentStatus[] => [
    ...attachments.map(({ file, uploadState }) => getUserFileAttachmentStatus({ file, uploadState }).status),
    ...pendingUploads.map(({ uploadState }) => getUserFileAttachmentStatus({ uploadState }).status),
  ]
)

const selectAgentConversationFormHasUnreadyUserFiles = createSelector([selectAgentConversationFormAttachedUserFileAttachmentStatuses], (statuses): boolean =>
  statuses.some((status) => status !== 'ready')
)

export const selectAgentConversationFormHasProcessingUserFiles = createSelector(
  [selectAgentConversationFormVisibleUserFileAttachmentStatuses],
  (statuses): boolean => statuses.some((status) => status === 'uploading' || status === 'processing')
)

export const selectAgentConversationFormHasErroredUserFiles = createSelector(
  [selectAgentConversationFormVisibleUserFileAttachmentStatuses],
  (statuses): boolean => statuses.some((status) => status === 'error')
)

/**
 * Agent Conversation Form Submit Blocked
 * Check if the agent conversation form submission is blocked.
 *
 * @returns The reason the form is blocked or null if it is not blocked.
 */
export const selectAgentConversationFormSubmitBlockedReason = createSelector(
  // Input 1: Get the conversation based on chatId
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId] ?? null,

  // Input 2: Provide the processing statuses of the selected files
  (state: RootState, props: { keys: string[] }) => selectFileProcessingStatusesWithKeys(state, props),

  // Input 3: The websocket connection for this conversation
  (state: RootState, props: { conversationId: string }) => state.ws2State.connections[props.conversationId],

  // Input 4: Flag to allow empty input text
  // This is used to bypass the empty text input check when selecting follow up questions
  (_state: RootState, props: { allowEmptyInput?: boolean }) => props.allowEmptyInput ?? false,

  // Input 5: Selected user-file readiness for new user-files uploads/selections
  selectAgentConversationFormHasUnreadyUserFiles,

  // Output: Check if the form is blocked
  (form, processingStatuses, connection, allowEmptyInput, hasUnreadyUserFiles): string | null => {
    if (!form) return 'no form'

    // Block if a query is currently being submitted
    if (form.queryIsSubmitting) return 'previous query submission in progress'

    // Block if already connected
    if (connection?.connected) return 'websocket connected - previous message turn in progress'

    // Block if input text is empty, unless allowEmptyInput flag is true
    if (!allowEmptyInput && form.requestParams.input_text.trim() === '') return 'input text is empty'

    // Block if selected files are not ready
    const hasUnprocessedFiles = Object.values(processingStatuses).some((status) =>
      [FileProcessingStatus.PENDING, FileProcessingStatus.PROCESSING, FileProcessingStatus.ERROR].includes(status as FileProcessingStatus)
    )
    if (hasUnprocessedFiles || hasUnreadyUserFiles) return 'selected files are not ready'

    // Allow submission
    return null
  }
)

/**
 * Select Agent Conversation Form File Paths
 * Get the list of files that are marked as files for a conversation
 *
 * @returns The list of file paths
 */
export const selectAgentConversationFormFilePaths = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.file_paths ?? []
)

/**
 * Select Agent Conversation Form Template Files Paths
 * Get the list of files that are marked as templates for a conversation
 *
 * @returns The list of template file paths
 */
export const selectAgentConversationFormTemplateFilePaths = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.template_paths ?? []
)

/**
 * Select Agent Conversation Form Template Mode
 * Get the global template mode flag state
 *
 * @returns Boolean indicating if template mode is active
 */
export const selectAgentConversationFormTemplateModeActive = (state: RootState): boolean => {
  return state.agentConversationFormState.isTemplateMode
}

/**
 * Select Agent Conversation Form Caselaw Search Filter
 * Get the caselaw search filter for a conversation
 *
 * @returns The caselaw search filter or null if it is not set
 */
export const selectAgentConversationFormCaselawSearchFilter = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  // Make the check for the jurisdiction codes to be not empty
  (form) => {
    const filter = form?.requestParams?.caselaw_search_filter
    if (filter && Array.isArray(filter.jurisdiction_codes) && filter.jurisdiction_codes.length > 0) {
      return filter
    }
    return null
  }
)

/**
 * Select Agent Conversation Form Caselaw Date Range
 * Get the caselaw date range for a conversation
 *
 * @returns Object with dateLow and dateHigh in YYYY-MM format, or { dateLow: null, dateHigh: null } if not set
 */
export const selectAgentConversationFormCaselawDateRange = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    const filter = form?.requestParams?.caselaw_search_filter
    return {
      dateLow: filter?.date_low ?? null,
      dateHigh: filter?.date_high ?? null,
    }
  }
)

/**
 * Select Agent Conversation Form LRR Search Filter
 * Get the lrr search filter for a conversation
 *
 * @returns The lrr search filter or null if it is not set
 */
export const selectAgentConversationFormLRRSearchFilter = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    const filter = form?.requestParams?.lrr_search_filter
    if (filter && Array.isArray(filter.jurisdiction_codes) && filter.jurisdiction_codes.length > 0) {
      return filter
    }
    return null
  }
)

/**
 * Select whether a conversation already has jurisdiction filter params.
 *
 * Explicit empty filters count as present so Clear All does not fall back to matter/user defaults.
 */
export const selectAgentConversationFormHasJurisdictionFilters = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    return Boolean(form?.requestParams.lrr_search_filter || form?.requestParams.caselaw_search_filter)
  }
)

export const selectDefaultResearchJurisdictionForConversation = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationsState.conversations[props.conversationId]?.matter_id ?? null,
  (state: RootState) => state.mattersState.matters,
  (matterId, matters) => {
    if (!matterId) return null

    const matter = matters[matterId]
    if (!matter) return null

    return matter.default_jurisdiction
  }
)

/**
 * Select the total of sources selected minus the state LRR sources
 *
 * @returns The number of sources selected
 */
export const selectAgentConversationFormNumberOfSourcesSelected = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormLRRSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawSearchFilter(state, props),
  (lrrSearchFilter, caselawSearchFilter) => {
    const stateLrrSources = getStateLrrSources(inferSelectedStateFromCourtIds(caselawSearchFilter?.jurisdiction_codes ?? null))

    // Check if stateLRR sources are present
    if (stateLrrSources && stateLrrSources.length > 0) {
      return (lrrSearchFilter?.jurisdiction_codes?.length ?? 0) + (caselawSearchFilter?.jurisdiction_codes?.length ?? 0) - stateLrrSources.length
    }

    const totalSourcesSelected = (lrrSearchFilter?.jurisdiction_codes?.length ?? 0) + (caselawSearchFilter?.jurisdiction_codes?.length ?? 0)

    return totalSourcesSelected
  }
)

/**
 * Select Agent Conversation Form Related Federal Content Selection State
 * Checks if all expected federal courts AND federal LRR sources for the selected state are included
 * Federal courts should be in caselaw_search_filter, LRR sources in lrr_search_filter
 *
 * @returns Object with selection state: { allSelected }
 */
export const selectAgentConversationFormRelatedFederalContentState = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormLRRSearchFilter(state, props),
  (caselawSearchFilter, lrrSearchFilter) => {
    // Get the selected state jurisdiction code
    const stateCourtIds = caselawSearchFilter?.jurisdiction_codes
    const selectedState = inferSelectedStateFromCourtIds(stateCourtIds ?? null)

    // Get all expected federal court IDs for the selected state (state-specific + standard)
    const stateFederalCourts = selectedState ? getFederalCourtIdsByState(selectedState) : []
    const standardFederalCourts = getStandardFederalCourtIds()
    const allExpectedFederalCourts = [...stateFederalCourts, ...standardFederalCourts]

    // Get standard federal LRR sources
    const federalLrrSources = getStandardFederalLRRSources()

    // If no state is selected, return allSelected as false
    if (!selectedState) {
      return { allSelected: false }
    }

    // Get currently selected codes from both filters
    const selectedCaselawCodes = caselawSearchFilter?.jurisdiction_codes ?? []
    const selectedLrrCodes = lrrSearchFilter?.jurisdiction_codes ?? []

    // Check if all expected federal courts are in caselaw filter
    const allExpectedFederalCourtsSelected = allExpectedFederalCourts.every((code) => selectedCaselawCodes.includes(code))

    // Check if all federal LRR sources are in LRR filter
    const allFederalLrrSourcesSelected = federalLrrSources.every((source) => selectedLrrCodes.includes(source))

    return {
      allSelected: allExpectedFederalCourtsSelected && allFederalLrrSourcesSelected,
    }
  }
)

/**
 * Select Agent Conversation Form Research Enabled
 * Get values from the caselaw and lrr search filters and check if research is enabled
 *
 * Research is enabled when caselaw search filter or lrr search filter has jurisdiction codes
 *
 * @returns true if research is enabled, false otherwise
 */
export const selectAgentConversationFormResearchEnabled = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormLRRSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawDateRangeValid(state, props),
  (caselawSearchFilter, lrrSearchFilter, dateRangeValid) => {
    return dateRangeValid && ((caselawSearchFilter?.jurisdiction_codes?.length ?? 0) > 0 || (lrrSearchFilter?.jurisdiction_codes?.length ?? 0) > 0)
  }
)

/**
 * Select Agent Conversation Form Caselaw Date Range Is Valid
 * Check if the caselaw date range is valid (dateLow is before dateHigh)
 *
 * @returns true if the date range is valid, false otherwise
 */
export const selectAgentConversationFormCaselawDateRangeValid = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawDateRange(state, props),
  (dateRange) => {
    // If both are null, range is valid (no filtering)
    if (!dateRange?.dateLow && !dateRange?.dateHigh) {
      return true
    }
    // If either is set but invalid, range is invalid
    if ((dateRange?.dateLow && !dayjs(dateRange?.dateLow).isValid()) || (dateRange?.dateHigh && !dayjs(dateRange?.dateHigh).isValid())) {
      return false
    }
    // Check if dateLow is before dateHigh
    return !dateRange?.dateLow || !dateRange?.dateHigh || dayjs(dateRange?.dateLow).isBefore(dayjs(dateRange?.dateHigh))
  }
)

/**
 * Select if include unpublished cases are included
 *
 * @returns true if unpublished cases are included, false otherwise
 */
export const selectAgentConversationFormIncludeUnpublishedCases = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => form?.requestParams.caselaw_search_filter?.include_unpublished_cases ?? false
)
